/**
 * ============================================================================
 * ADMIN ESCROW DETAIL PAGE - 100% Ready
 * ============================================================================
 * 
 * Admin page for viewing and managing a single escrow
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAuthContext } from '../../providers';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import Input from '../../components/common/Input';

/**
 * Admin Escrow Detail Component
 * @returns {JSX.Element}
 */
const AdminEscrowDetail = () => {
  const { escrowId } = useParams();
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuthContext();
  const { getEscrowDetails, adminPartialRelease, adminRefundEscrow, loading } = useAdmin();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [escrow, setEscrow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [partialData, setPartialData] = useState({
    sellerAmount: '',
    buyerAmount: '',
    reason: '',
    releaseNotes: '',
  });
  const [refundData, setRefundData] = useState({
    refundReason: '',
    refundNotes: '',
  });

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (authUser?.role !== 'admin' && authUser?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, authUser, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await getEscrowDetails(escrowId);
        setEscrow(response?.escrow || response?.data);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement de l\'escrow' : 'Failed to load escrow')
        );
        console.error('Load escrow error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (escrowId && isAuthenticated && (authUser?.role === 'admin' || authUser?.role === 'super_admin')) {
      loadData();
    }
  }, [escrowId, isAuthenticated, authUser, getEscrowDetails, showError, language]);

  const handlePartialRelease = async () => {
    if (!escrow) return;

    try {
      const sellerAmount = parseFloat(partialData.sellerAmount);
      const buyerAmount = parseFloat(partialData.buyerAmount);
      const totalAmount = parseFloat(escrow.amount || 0);

      if (sellerAmount + buyerAmount !== totalAmount) {
        showError(
          language === 'fr'
            ? 'Les montants doivent être égaux au montant total de l\'escrow'
            : 'Amounts must equal the total escrow amount'
        );
        return;
      }

      if (!partialData.reason || partialData.reason.length < 10) {
        showError(
          language === 'fr'
            ? 'La raison doit contenir au moins 10 caractères'
            : 'Reason must be at least 10 characters'
        );
        return;
      }

      await adminPartialRelease(escrowId, {
        seller_amount: sellerAmount,
        buyer_amount: buyerAmount,
        reason: partialData.reason,
        release_notes: partialData.releaseNotes,
      });

      showSuccess(
        language === 'fr' ? 'Libération partielle effectuée!' : 'Partial release completed!'
      );
      setShowPartialModal(false);
      setPartialData({ sellerAmount: '', buyerAmount: '', reason: '', releaseNotes: '' });
      // Reload escrow
      const response = await getEscrowDetails(escrowId);
      setEscrow(response?.escrow || response?.data);
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec de la libération' : 'Failed to release'));
    }
  };

  const handleRefund = async () => {
    if (!escrow) return;

    try {
      if (!refundData.refundReason || refundData.refundReason.length < 10) {
        showError(
          language === 'fr'
            ? 'La raison du remboursement doit contenir au moins 10 caractères'
            : 'Refund reason must be at least 10 characters'
        );
        return;
      }

      await adminRefundEscrow(escrowId, {
        refund_reason: refundData.refundReason,
        refund_notes: refundData.refundNotes,
      });

      showSuccess(language === 'fr' ? 'Remboursement effectué!' : 'Refund completed!');
      setShowRefundModal(false);
      setRefundData({ refundReason: '', refundNotes: '' });
      // Reload escrow
      const response = await getEscrowDetails(escrowId);
      setEscrow(response?.escrow || response?.data);
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec du remboursement' : 'Failed to refund'));
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      pending: 'warning',
      funded: 'success',
      frozen: 'error',
      released: 'success',
      refunded: 'info',
      partial: 'info',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      funded: '🔒',
      frozen: '❄️',
      released: '✅',
      refunded: '↩️',
      partial: '📊',
      cancelled: '❌',
    };
    return icons[status] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'from-yellow-500 to-orange-500',
      funded: 'from-blue-500 to-cyan-500',
      frozen: 'from-red-500 to-pink-500',
      released: 'from-green-500 to-emerald-500',
      refunded: 'from-purple-500 to-pink-500',
      partial: 'from-indigo-500 to-purple-500',
      cancelled: 'from-gray-500 to-slate-500',
    };
    return colors[status] || 'from-gray-500 to-slate-500';
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!escrow) {
    return (
      <AdminLayout>
        <Card variant="glass" className="p-12">
          <EmptyState
            icon="🔒"
            title={language === 'fr' ? 'Escrow introuvable' : 'Escrow not found'}
            description={
              language === 'fr'
                ? 'L\'escrow demandé n\'existe pas ou a été supprimé'
                : 'The requested escrow does not exist or has been deleted'
            }
          />
        </Card>
      </AdminLayout>
    );
  }

  const canPartialRelease = escrow.status === 'funded' || escrow.status === 'frozen';
  const canRefund = escrow.status === 'funded' || escrow.status === 'frozen';

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/escrows')}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour aux escrows' : 'Back to Escrows'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          🔒 {language === 'fr' ? 'Détails de l\'Escrow' : 'Escrow Details'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Gérez et surveillez cet escrow'
            : 'Manage and monitor this escrow'}
        </p>
      </div>

      {/* Escrow Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <Card variant="glass">
            <div className="space-y-6">
              {/* Status Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${getStatusColor(escrow.status)} flex items-center justify-center text-4xl shadow-2xl`}>
                    {getStatusIcon(escrow.status)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-1">
                      {language === 'fr' ? 'Escrow' : 'Escrow'} #{escrow.id?.slice(0, 8) || 'N/A'}
                    </h2>
                    <StatusBadge variant={getStatusVariant(escrow.status)} size="lg">
                      {escrow.status}
                    </StatusBadge>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {language === 'fr' ? 'Montant' : 'Amount'}
                </p>
                <p className="text-3xl font-black text-gray-900 dark:text-white">
                  {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
                </p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {escrow.transactionId && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Transaction' : 'Transaction'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      #{escrow.transactionId?.slice(0, 8) || 'N/A'}
                    </p>
                  </Card>
                )}
                {escrow.createdAt && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Créé le' : 'Created At'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(escrow.createdAt)}
                    </p>
                  </Card>
                )}
                {escrow.fundedAt && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Financé le' : 'Funded At'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(escrow.fundedAt)}
                    </p>
                  </Card>
                )}
                {escrow.releasedAt && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Libéré le' : 'Released At'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(escrow.releasedAt)}
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <Card variant="glass">
            <h3 className="font-black text-gray-900 dark:text-white mb-4">
              {language === 'fr' ? 'Actions Admin' : 'Admin Actions'}
            </h3>
            <div className="space-y-3">
              {canPartialRelease && (
                <Button
                  variant="gradient"
                  onClick={() => {
                    setPartialData({
                      sellerAmount: '',
                      buyerAmount: '',
                      reason: '',
                      releaseNotes: '',
                    });
                    setShowPartialModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 shadow-xl hover:shadow-2xl transition-all"
                >
                  📊 {language === 'fr' ? 'Libération Partielle' : 'Partial Release'}
                </Button>
              )}
              {canRefund && (
                <Button
                  variant="gradient"
                  onClick={() => {
                    setRefundData({ refundReason: '', refundNotes: '' });
                    setShowRefundModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
                >
                  ↩️ {language === 'fr' ? 'Rembourser' : 'Refund'}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/transactions/${escrow.transactionId}`)}
                className="w-full"
              >
                👁️ {language === 'fr' ? 'Voir Transaction' : 'View Transaction'}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Partial Release Modal */}
      <Modal
        isOpen={showPartialModal}
        onClose={() => {
          setShowPartialModal(false);
          setPartialData({ sellerAmount: '', buyerAmount: '', reason: '', releaseNotes: '' });
        }}
        title={language === 'fr' ? 'Libération Partielle' : 'Partial Release'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? `Montant total de l'escrow: ${formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}`
                : `Total escrow amount: ${formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}`}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Montant Vendeur' : 'Seller Amount'}
            </label>
            <Input
              type="number"
              value={partialData.sellerAmount}
              onChange={(e) =>
                setPartialData((prev) => ({ ...prev, sellerAmount: e.target.value }))
              }
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Montant Acheteur' : 'Buyer Amount'}
            </label>
            <Input
              type="number"
              value={partialData.buyerAmount}
              onChange={(e) =>
                setPartialData((prev) => ({ ...prev, buyerAmount: e.target.value }))
              }
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Raison (requis, min 10 caractères)' : 'Reason (required, min 10 chars)'}
            </label>
            <Textarea
              value={partialData.reason}
              onChange={(e) => setPartialData((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder={language === 'fr' ? 'Expliquez la raison de la libération partielle...' : 'Explain the reason for partial release...'}
              rows={4}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
            </label>
            <Textarea
              value={partialData.releaseNotes}
              onChange={(e) =>
                setPartialData((prev) => ({ ...prev, releaseNotes: e.target.value }))
              }
              placeholder={language === 'fr' ? 'Notes supplémentaires...' : 'Additional notes...'}
              rows={3}
            />
          </div>
          <div className="flex gap-2 pt-4">
            <Button
              variant="gradient"
              onClick={handlePartialRelease}
              loading={loading}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 shadow-xl hover:shadow-2xl transition-all"
            >
              ✅ {language === 'fr' ? 'Confirmer' : 'Confirm'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowPartialModal(false);
                setPartialData({ sellerAmount: '', buyerAmount: '', reason: '', releaseNotes: '' });
              }}
              className="flex-1"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Refund Modal */}
      <Modal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundData({ refundReason: '', refundNotes: '' });
        }}
        title={language === 'fr' ? 'Rembourser l\'Escrow' : 'Refund Escrow'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? `Montant à rembourser: ${formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}`
                : `Amount to refund: ${formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}`}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Raison du Remboursement (requis, min 10 caractères)' : 'Refund Reason (required, min 10 chars)'}
            </label>
            <Textarea
              value={refundData.refundReason}
              onChange={(e) =>
                setRefundData((prev) => ({ ...prev, refundReason: e.target.value }))
              }
              placeholder={language === 'fr' ? 'Expliquez la raison du remboursement...' : 'Explain the reason for refund...'}
              rows={4}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
            </label>
            <Textarea
              value={refundData.refundNotes}
              onChange={(e) =>
                setRefundData((prev) => ({ ...prev, refundNotes: e.target.value }))
              }
              placeholder={language === 'fr' ? 'Notes supplémentaires...' : 'Additional notes...'}
              rows={3}
            />
          </div>
          <div className="flex gap-2 pt-4">
            <Button
              variant="gradient"
              onClick={handleRefund}
              loading={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
            >
              ✅ {language === 'fr' ? 'Confirmer' : 'Confirm'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRefundModal(false);
                setRefundData({ refundReason: '', refundNotes: '' });
              }}
              className="flex-1"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminEscrowDetail;
